import { ReactNode, useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import { supabase } from "@/integrations/supabase/client";

interface ProtectedRouteProps {
  children: ReactNode;
  requireRole?: string;
}

export const ProtectedRoute = ({ children, requireRole }: ProtectedRouteProps) => {
  const { user, loading, hasRole } = useAuth();
  const { isTrialExpired, canAccessDashboard, loading: trialLoading } = useTrialStatus();
  const location = useLocation();
  const [emailVerified, setEmailVerified] = useState<boolean | null>(null);
  const [checkingVerification, setCheckingVerification] = useState(true);

  useEffect(() => {
    const checkEmailVerification = async () => {
      if (!user) {
        setCheckingVerification(false);
        return;
      }

      try {
        const { data: profile, error } = await supabase
          .from("profiles")
          .select("email_verified")
          .eq("id", user.id)
          .single();

        if (error) {
          console.error("Error checking email verification:", error);
          // If profile doesn't exist, assume not verified
          setEmailVerified(false);
        } else {
          setEmailVerified(profile?.email_verified ?? false);
        }
      } catch (err) {
        console.error("Error in verification check:", err);
        setEmailVerified(false);
      } finally {
        setCheckingVerification(false);
      }
    };

    checkEmailVerification();
  }, [user]);

  if (loading || trialLoading || checkingVerification) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Check if email is verified (site admins bypass this)
  const isSiteAdmin = hasRole('site_admin');
  if (!isSiteAdmin && emailVerified === false) {
    return <Navigate to={`/check-email?email=${encodeURIComponent(user.email || '')}`} replace />;
  }

  // Check trial status and restrict access if trial expired
  if (isTrialExpired && !canAccessDashboard(location.pathname)) {
    return <Navigate to="/trial-expired" replace />;
  }

  // Site admins have access to everything
  if (requireRole && !isSiteAdmin && !hasRole(requireRole)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};
